import { ZERO_VALUE } from "@utils/constants";

const RIGHT_PADDING = 50;
const LEFT_PADDING = 50;
const FULL_OPACITY = 1;
const MEDIUM_OPACITY = 0.8;
const PERCENTAGE = 100;
const MAX_VALUE_LENGTH = 10;
const STROKE_WIDTH = 3;
const SHOW_LABEL_WIDTH = 1300;

const transformCurrencyStringToNumber = (currencyString) => {
  return parseFloat(currencyString.replace(/[^0-9.-]+/g, ""));
};

const calculateAttributeColor = (attr) => {
  // let attributeColor = "text-gray-700";
  let attributeColor = "text-gray-900 dark:text-white";
  if (attr.Tag === "VARIANCE_AMOUNT") {
    // attributeColor = attr?.IsNegative ? "text-red-500" : "text-green-500";
    const numberValue = transformCurrencyStringToNumber(attr?.Value);
    attributeColor =
      numberValue === ZERO_VALUE ? "text-green-500" : "text-red-500";
  }
  return `<span class="${attributeColor}">${attr.Value}</span>`;
};

const PopUpBuilder = ({
  dataPointLabel = "",
  label = "",
  description = "",
  attributes = [],
  parentClass = "popup-content",
}) => {
  let popupContent = `<div class="${parentClass}">
                        
                        <h3 class="flex justify-between items-center dark:text-white">
                        `;
  if (label) {
    popupContent += `<b><u>${label}</u></b>`;
  }

  if (dataPointLabel) {
    popupContent += `<b class="text-primary-400 text-center">${dataPointLabel}</b>`;
  }

  popupContent += `</h3>`;

  if (description) {
    popupContent += `<p>${description}</p>`;
  }

  if (attributes.length > ZERO_VALUE) {
    popupContent += `<ul class="mt-2 gap-2 dark:text-white">`;
    attributes.forEach((attr) => {
      popupContent += `<li>
                        ${attr.Label}:
                        <b>${calculateAttributeColor(attr)}</b> 
                      </li>`;
    });
    popupContent += `</ul>`;
  }
  popupContent += `</div>`;
  return popupContent;
};

const generalShapes = [
  "circle",
  "square",
  // "line",
  // "plus",
  // "cross",
  // "star",
  "sparkle",
  "diamond",
  "triangle",
];
export const analyzeData = (
  response,
  showPopup = () => {},
  fontFamily = "Inter",
  isDarkMode = false,
) => {
  const chartSettings = response.Chart;
  const dataSets = response.Chart.DataSets;
  const legends = response.Chart.Legends;

  // Extract X-axis categories
  const categories = chartSettings.XAxis.ScaleMarkers.map(
    (marker) => marker.Label
  ).reverse();

  const pointClick = (event, chartContext, { seriesIndex, dataPointIndex }) => {
    const dataSet = dataSets[seriesIndex];
    // const currentPage = document.getElementById(
    //   `current_page_${uniqueID}`
    // ).value;
    // const calculatedDataPointIndex =
    //   currentPage * maxSeriesPerPage + dataPointIndex;
    const dataPoint = dataSet?.Data[dataPointIndex];
    // this below line takes the dataPoint without considering the current page
    // const dataPoint = dataSet?.Data[dataPointIndex];
    const popup = dataPoint?.Popup;
    if (popup) {
      const label = popup?.Label || "";
      const description = popup?.ShortDescription || "";
      const popupContent = PopUpBuilder({
        dataPointLabel: dataPoint?.Label,
        label: label,
        description: description,
        attributes: popup.Attributes,
      });
      // Display the popup content
      showPopup(popupContent, seriesIndex, dataPointIndex);
    } else {
      // showPopup('<h3>No popup content</h3>');
    }
  };
  // Generate chartOptions
  const hideLabels = window.innerWidth <= SHOW_LABEL_WIDTH;

  const chartOptions = {
    chart: {
      type: "line",
      height: 350,
      zoom: {
        enabled: false, // Disable range zooming
      },
      animations: {
        enabled: true,
        easing: "easeinout",
        // speed: 1800,
        animateGradually: {
          enabled: true,
          delay: 250,
        },
        dynamicAnimation: {
          enabled: true,
          speed: 750,
        },
      },
      events: {
        markerClick: function (
          event,
          chartContext,
          { seriesIndex, dataPointIndex }
        ) {
          pointClick(event, chartContext, { seriesIndex, dataPointIndex });
        },
        click: function (event, chartContext, { seriesIndex, dataPointIndex }) {
          pointClick(event, chartContext, { seriesIndex, dataPointIndex });
        },
        legendClick: function (chartContext, seriesIndex, config) {
          const series = chartContext.w.globals.series;
          const seriesName = chartContext.w.globals.seriesNames[seriesIndex];
          const isVisible = chartContext.w.globals.series[seriesIndex].length;

          if (isVisible) {
            chartContext.hideSeries(seriesName);
          } else {
            chartContext.showSeries(seriesName);
          }
        },
        xAxisLabelClick: function (
          pointerEvent,
          opts,
          { seriesIndex, labelIndex }
        ) {
          const marker = chartSettings.XAxis.ScaleMarkers[labelIndex];

          const popup = marker.Popup;
          if (popup) {
            const label = popup?.Label || "";
            const description = popup?.ShortDescription || "";
            let popupContent = PopUpBuilder({
              dataPointLabel: marker?.Label,
              label: label,
              description: description,
              attributes: popup.Attributes,
            });

            // Display the popup content
            showPopup(
              popupContent,
              seriesIndex,
              labelIndex,
              chartSettings.XAxis.ScaleMarkers
            );
          }
        },
      },
      toolbar: {
        show: true,
        offsetX: 0,
        offsetY: 0,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
        export: {
          csv: {
            filename: chartSettings.Label,
            columnDelimiter: ",",
          },
          svg: {
            filename: chartSettings.Label,
          },
          png: {
            filename: chartSettings.Label,
          },
        },
        // autoSelected: 'zoom'
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "30%", // Narrow down the width of the bars
        endingShape: "rounded",
      },
    },
    dataLabels: {
      // enabled: !hideLabels,
      enabled: false,
      enabledOnSeries: dataSets
        .map((set, index) =>
          set.ElementTag === "LINE" || set.ElementTag === "BAR" ? index : null
        )
        .filter((index) => index !== null), // Enable data labels only for the line series
      offsetX: 0,
      offsetY: -5, // Adjust the vertical position of the data labels
      style: {
        fontSize: "12px",
        fontFamily: fontFamily,
        fontWeight: "normal",
        colors: ["#ffffff99"],
        cursor: "pointer",
      },
      background: {
        enabled: true,
        foreColor: "black",
        padding: 2,
        borderRadius: 0,
        borderWidth: 0,
        borderColor: "#d6d6d6",
        opacity: 1,
        dropShadow: {
          enabled: false,
          top: 1,
          left: 1,
          blur: 1,
          // color: "#000",
          opacity: 0.45,
        },
      },
      dropShadow: {
        enabled: false,
        top: 0,
        left: 0,
        blur: 1,
        color: "black",
        opacity: 0.3,
      },
      formatter: function (val, opts) {
        const seriesIndex = opts.seriesIndex;
        const dataPointIndex = opts.dataPointIndex;
        const dataSet = dataSets[seriesIndex];
        const dataPoint = dataSet.Data[dataPointIndex];
        return dataSet.ShowDataLabel
          ? dataPoint?.Label || ""
          : dataSet.ShowDataValue
          ? dataPoint?.Value || ""
          : "";
      },
    },
    stroke: {
      show: true,
      width: dataSets.map((set) =>
        set.ElementTag === "LINE" ? STROKE_WIDTH : ZERO_VALUE
      ),
      curve: "smooth", // Make the lines straight without border radius
    },
    markers: {
      strokeColors: "#444343",
      strokeWidth: 0,
      strokeOpacity: 0,
      strokeDashArray: 0,
      fillOpacity: 1,
      size: 5, // Size of the markers
      // shape: "triangle", // Shape of the markers
      // shape: "cross", // Shape of the markers
      shape: generalShapes, // Shape of the markers
      hover: {
        size: 7, // Size of the markers on hover
        sizeOffset: 3, // Offset of the markers on hover
      },
    },
    // colors: dataSets.map((set) => set.ElementColor), // Use colors from the dataset
    colors: legends.map((set) => set.KeyColor), // Use colors from legends
    // colors: [
    //   "#0F4EDB",
    //   "#2CCCFC",
    //   "#FDCB38",
    //   "#FC9A45",
    //   "#CCCCCC",
    // ],
    // title: {
    //   text: chartSettings.Label,
    //   align: "center",
    //   margin: 30,
    //   offsetX: 0,
    //   offsetY: 0,
    //   floating: false,
    //   style: {
    //     fontSize: "22px",
    //     // fontWeight: "bold",
    //     fontFamily: undefined,
    //     color: "#263238",
    //   },
    // },
    xaxis: {
      categories: categories,
      title: {
        text: chartSettings.XAxis.Label,
        offsetY: 5,
        style: {
          color: isDarkMode ? "#e5e7eb" : "#c7c7c7",
          fontSize: "10px",
          fontFamily: fontFamily,
          // fontWeight: 500,
          // cssClass: "font-semibold text-md",
        },
      },
      // reversed: true,
      labels: {
        useHTML: true,
        style: {
          fontSize: "12px",
          fontFamily: fontFamily,
          fontWeight: 500,
          colors: isDarkMode ? "#ffffff" : undefined,
          cssClass: "cursor-pointer",
        },
        formatter: function (value) {
          if (value && value.includes("\r\n")) {
            return value.split("\r\n");
          }
          return hideLabels
            ? value && value.length > MAX_VALUE_LENGTH
              ? `${value.substring(ZERO_VALUE, MAX_VALUE_LENGTH)}..`
              : value
            : value;

          // const formattedValue = value
          //   ? value.replace(/\r\n/g, "<br/>")
          //   : value;
          // return hideLabels
          //   ? formattedValue && formattedValue.length > 10
          //     ? `${formattedValue.substring(0, 10)}..`
          //     : formattedValue
          //   : formattedValue;
        },
        // formatter: function (value) {
        //   return '<span class="flex flex-col"><span>1</span><span>2</span></span>'
        // },
      },
    },
    yaxis: chartSettings.YAxis.map((axis) => ({
      title: {
        show: false,
        text: axis.Label,
        style: {
          color: isDarkMode ? "#e5e7eb" : "#c7c7c7",
          fontSize: "10px",
          fontFamily: fontFamily,
          // fontWeight: 500,
          // cssClass: "font-semibold text-md",
        },
      },
      max: axis.Tag.includes("PERCENTAGE") ? PERCENTAGE : undefined, // Use includes()
      opposite: axis.Position === "RIGHT",
      labels: {
        style: {
          colors: isDarkMode ? "#ffffff" : undefined,
        },
      },
    })),
    fill: {
      opacity: [FULL_OPACITY, MEDIUM_OPACITY, MEDIUM_OPACITY],
    },
    tooltip: {
      shared: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        // const currentPage = document.getElementById(
        //   `current_page_${uniqueID}`
        // ).value;
        // const calculatedDataPointIndex =
        //   currentPage * maxSeriesPerPage + dataPointIndex;
        const dataPoint = dataSets[seriesIndex].Data[dataPointIndex];
        if (dataPoint.Tooltip) {
          const label = dataPoint?.Tooltip?.Label || "";
          const description = dataPoint?.Tooltip?.ShortDescription || "";
          // let tooltipContent = `<div class="tooltip-content px-2 py-3">
          //                         <h3><b><u>${label}</u></b></h3>
          //                         <p>${description}</p>
          //                         <ul class="mt-2 gap-1">`;
          // dataPoint.Tooltip.Attributes.forEach((attr) => {
          //   tooltipContent += `<li>
          //                       <strong>${attr.Label}:</strong>
          //                       ${calculateAttributeColor(attr)}
          //                     </li>`;
          // });
          // tooltipContent += `</ul></div>`;
          return PopUpBuilder({
            dataPointLabel: dataPoint?.Label,
            label: label,
            description: description,
            attributes: dataPoint.Tooltip.Attributes,
            parentClass: "tooltip-content px-2 py-3 dark:bg-gray-800 dark:border-gray-600",
          });
        }
        return "";
      },
    },
    legend: {
      position: "bottom", // Move the legend to the bottom
      horizontalAlign: "center", // Center align the legend
      offsetY: 10,
      size: 2,
      itemMargin: {
        horizontal: 5,
        vertical: 5,
      },
      labels: {
        colors: isDarkMode ? "#ffffff" : undefined,
      },
      markers: {
        size: 6,
        // shape: "circle",
        shape: generalShapes,
      },

      formatter: function (seriesName, opts) {
        return [
          `<strong style="color: ${isDarkMode ? "#ffffff" : "inherit"}">${legends[opts.seriesIndex]?.Label || seriesName}</strong>`,
        ];
      },
    },
    grid: {
      show: true,
      borderColor: "#d6d6d680",
      strokeDashArray: 1,
      position: "back",
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: 0,
        right: hideLabels ? ZERO_VALUE : RIGHT_PADDING,
        bottom: 10,
        left: hideLabels ? ZERO_VALUE : LEFT_PADDING,
      },
    },
  };

  // Generate chartSeries
  const chartSeries = dataSets.map((set) => {
    const reversedSet = set.Data.reverse();
    return {
      name: set.Label,
      type: set.ElementTag.toLowerCase(),
      data: reversedSet.map((dataPoint) => dataPoint.Value),
      originalData: reversedSet,
    };
  });

  return { chartOptions, chartSeries };
};
